import { useState } from 'react'
import { SkeletonStack } from '@/shared/ui'
import { useStats } from '@/entities/stats'
import { DayChart, HourChart, OutcomeChart, TextList } from './Charts'
import styles from './Stats.module.css'

const PERIODS = [7, 30, 90] as const

/**
 * Сводка по звонкам.
 *
 * Порядок блоков — от «как дела вообще» к «что именно чинить»: числа,
 * динамика, итоги, часы, и в конце два списка, из которых видно, что
 * дописать в анкету.
 */
export function StatsPanel() {
  const [days, setDays] = useState<number>(30)
  const stats = useStats(days)

  if (stats.isPending) {
    return (
      <div className={styles.panel}>
        <div className={styles.body}>
          <SkeletonStack rows={4} height={44} />
        </div>
      </div>
    )
  }

  if (!stats.data || stats.data.totals.calls === 0) {
    return null
  }

  const { totals, byDay, byOutcome, byHour, objections, missed } = stats.data

  return (
    <>
      <div className={styles.panel}>
        <div className={styles.head}>
          <span className={styles.title}>Итого за период</span>
          <div className={styles.periods}>
            {PERIODS.map((value) => (
              <button
                key={value}
                type="button"
                className={`${styles.period} ${days === value ? styles.periodActive : ''}`}
                onClick={() => setDays(value)}
              >
                {value} дн
              </button>
            ))}
          </div>
        </div>

        <div className={styles.totals}>
          <Total
            label="Звонков"
            value={totals.calls}
            unit={`${totals.minutes} мин`}
            delta={totals.deltaCalls}
          />
          <Total
            label="Довели до дела"
            value={totals.winRate}
            unit="%"
            delta={totals.deltaWinRate}
            deltaUnit="п.п."
          />
          <Total label="Средняя оценка" value={totals.avgScore} unit="из 100" delta={totals.deltaScore} />
          <Total
            label="Агент отвечает"
            value={totals.replyMs ? Number((totals.replyMs / 1000).toFixed(1)) : 0}
            unit="с"
            delta={totals.deltaReply ? Number((totals.deltaReply / 1000).toFixed(1)) : totals.deltaReply}
            // Для времени ответа рост — это плохо: агент стал думать
            // дольше. У остальных показателей наоборот.
            lowerIsBetter
          />
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.head}>
          <span className={styles.title}>Звонки по дням</span>
        </div>
        <div className={styles.body}>
          <DayChart points={byDay} />
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.head}>
            <span className={styles.title}>Чем кончались</span>
          </div>
          <div className={styles.body}>
            <OutcomeChart items={byOutcome} />
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.head}>
            <span className={styles.title}>Когда звонили</span>
          </div>
          <div className={styles.body}>
            <HourChart points={byHour} />
          </div>
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.head}>
            <span className={styles.title}>Что возражают</span>
          </div>
          <div className={styles.body}>
            <TextList items={objections} empty="Возражений пока не было" />
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.head}>
            <span className={styles.title}>Что агент упускает</span>
          </div>
          <div className={styles.body}>
            <TextList items={missed} empty="Разбор не нашёл промахов" />
          </div>
        </div>
      </div>
    </>
  )
}

interface TotalProps {
  label: string
  value: number
  unit?: string
  delta: number | null
  deltaUnit?: string
  /** Для времени ответа рост — ухудшение. */
  lowerIsBetter?: boolean
}

function Total({ label, value, unit, delta, deltaUnit, lowerIsBetter }: TotalProps) {
  const better = delta === null ? null : lowerIsBetter ? delta < 0 : delta > 0

  return (
    <div className={styles.total}>
      <div className={styles.totalLabel}>{label}</div>
      <div className={styles.totalValue}>
        <span className={styles.number}>{value}</span>
        {unit && <span className={styles.unit}>{unit}</span>}
      </div>

      {delta !== null && delta !== 0 && (
        <span
          className={`${styles.delta} ${better ? styles.deltaUp : styles.deltaDown}`}
          title="по сравнению с предыдущим таким же периодом"
        >
          {delta > 0 ? <ArrowUp /> : <ArrowDown />}
          {Math.abs(delta)}
          {deltaUnit ? ` ${deltaUnit}` : ''}
        </span>
      )}
    </div>
  )
}

function ArrowUp() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 8.5V1.5M5 1.5L2 4.5M5 1.5L8 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ArrowDown() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 1.5V8.5M5 8.5L2 5.5M5 8.5L8 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
